/**
 * Validaciones del asistente de configuración inicial.
 *
 * Todas las consultas van directo al servidor: la caché local de Firestore podría responder con
 * datos viejos (un cliente dado de baja, una sesión ya cerrada) y dejar pasar una configuración
 * inválida.
 */
import { collection, doc, getDocFromServer, getDocsFromServer, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";

/**
 * @param {string} env
 * @param {string} clientId
 * @returns {Promise<{ok: true, data: Object} | {ok: false, error: 'not_found' | 'network'}>}
 */
export async function checkClient(env, clientId) {
  try {
    const snapshot = await getDocFromServer(doc(db, "kiosk", env, "client", clientId));
    if (!snapshot.exists()) return { ok: false, error: "not_found" };
    return { ok: true, data: { id: snapshot.id, ...snapshot.data() } };
  } catch (error) {
    console.error("[Setup] Error checking client:", error);
    return { ok: false, error: "network" };
  }
}

// Un tótem encendido hace ping cada minuto (PING_TIME en App.jsx). Una sesión activa sin ping hace
// más de esto quedó abierta porque el equipo se apagó o se cayó sin cerrarla; con margen para
// pings atrasados y relojes de equipo desfasados.
export const STALE_SESSION_MS = 5 * 60 * 1000;

const toMillis = (value) => (typeof value?.toMillis === "function" ? value.toMillis() : null);

/**
 * Una sesión activa está abandonada si su último ping (o, sin ping, su inicio) es más viejo que
 * STALE_SESSION_MS. Sin ninguna de las dos fechas también se considera abandonada.
 */
export function isSessionStale(session, nowMs = Date.now()) {
  const lastSeen = toMillis(session?.last_ping) ?? toMillis(session?.started_at);
  return lastSeen === null || nowMs - lastSeen > STALE_SESSION_MS;
}

/**
 * Revisa que la instancia exista y que ningún otro tótem la esté usando.
 *
 * Solo bloquea una sesión activa con ping reciente: las abandonadas (equipo apagado sin cerrar
 * sesión) se ignoran, y el tótem las cierra al abrir la suya (createSession en App.jsx).
 *
 * @param {string} env
 * @param {string} clientId
 * @param {string} instanceId
 * @returns {Promise<{ok: true, data: Object} | {ok: false, error: 'not_found' | 'in_use' | 'network'}>}
 */
export async function checkInstance(env, clientId, instanceId) {
  try {
    const snapshot = await getDocFromServer(doc(db, "kiosk", env, "client", clientId, "instances", instanceId));
    if (!snapshot.exists()) return { ok: false, error: "not_found" };

    const sessionsRef = collection(db, "kiosk", env, "client", clientId, "sessions");
    const activeSessions = await getDocsFromServer(query(sessionsRef, where("instance", "==", instanceId), where("active", "==", true)));
    if (activeSessions.docs.some((session) => !isSessionStale(session.data()))) return { ok: false, error: "in_use" };

    return { ok: true, data: snapshot.data() };
  } catch (error) {
    console.error("[Setup] Error checking instance:", error);
    return { ok: false, error: "network" };
  }
}

/**
 * Obtiene el método de pago con el que se probará el POS: el marcado para el asistente en el
 * dashboard (`useInSetupWizard`) o, si no hay, el primero que no sea efectivo.
 *
 * Los métodos vienen de `config/styles`, que tiene una lista para el tema claro
 * (`global.paymentMethods`) y otra para el oscuro (`dark.global.paymentMethods`); se usa la del
 * tema activo, igual que el resto de la app.
 *
 * @param {string} env
 * @param {string} clientId
 * @param {'light' | 'dark'} theme
 * @returns {Promise<{ok: true, data: Object} | {ok: false, error: 'no_pos_method' | 'network'}>}
 */
export async function getPOSPaymentMethod(env, clientId, theme) {
  try {
    const snapshot = await getDocFromServer(stylesRef(env, clientId));
    return pickPOSPaymentMethod(snapshot.exists() ? snapshot.data() : null, theme);
  } catch (error) {
    console.error("[Setup] Error loading payment methods:", error);
    return { ok: false, error: "network" };
  }
}

/**
 * Igual que getPOSPaymentMethod, pero escucha `config/styles` y vuelve a notificar cada vez que
 * cambian los métodos de pago (p. ej. al elegir otro método para el asistente en el dashboard).
 *
 * @param {(result: {ok: true, data: Object} | {ok: false, error: string}) => void} onResult
 * @returns {() => void} Cancela la escucha.
 */
export function watchPOSPaymentMethod(env, clientId, theme, onResult) {
  return onSnapshot(stylesRef(env, clientId), (snapshot) => {
    onResult(pickPOSPaymentMethod(snapshot.exists() ? snapshot.data() : null, theme));
  }, (error) => {
    console.error("[Setup] Error listening to payment methods:", error);
    onResult({ ok: false, error: "network" });
  });
}

const stylesRef = (env, clientId) => doc(db, "kiosk", env, "client", clientId, "config", "styles");

function pickPOSPaymentMethod(styles, theme) {
  const themeStyle = (theme === "dark" ? styles?.dark : styles) || styles;
  const paymentMethods = themeStyle?.global?.paymentMethods || [];
  // El dashboard permite marcar un método con `useInSetupWizard`; si ninguno lo está, se usa el
  // primero que no sea efectivo.
  const posMethods = paymentMethods.filter((pm) => pm.type !== "CASH");
  const method = posMethods.find((pm) => pm.useInSetupWizard === true) || posMethods[0];
  if (!method) return { ok: false, error: "no_pos_method" };
  return { ok: true, data: method };
}
